import type { ReactNode } from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { ZENDESK_OPEN_EVENT } from "~/hec/analytics/zendesk-widget-loader";

import { buttonFunctions, coreButtonInputs } from "../cta/cta.helper";
import { BuilderLinkWrapper } from "../cta/linkWrapper";

interface IconLinkItem {
  icon: string;
  label: string;
  isRevShare?: boolean;
  clickFunction?: string;
  scrollTargetId?: string;
  isInternalLink?: boolean;
  pageReference?: unknown;
  queryParams?: Array<{ name: string; value: string }>;
  externalLinkReference?: unknown;
  openInNewTab?: boolean;
  id?: string;
}

interface LiveChatTriggerConfig {
  icon: string;
  label: string;
}

interface IconLinksGridProps {
  items?: IconLinkItem[];
  includeLiveChatTrigger?: boolean;
  liveChatTriggerConfig?: LiveChatTriggerConfig;
  liveChatTriggerItemNumber?: number;
}

const getBorderStyles = (index: number, totalRows: number): string => {
  const currentRow = Math.floor(index / 2) + 1;
  const isLastRow = currentRow === totalRows;
  const hasRightBorder = index % 2 === 0;
  return `p-5 text-center ${hasRightBorder ? "border-r-2" : ""} ${
    !isLastRow ? "border-b-2" : ""
  } border-gray-02`;
};

// COM-183: the Zendesk widget is lazy-loaded behind a facade, so `window.zE`
// no longer exists until the widget has been requested. Dispatch the open
// request instead — the mounted ZendeskChatFacade loads the snippet (if it
// hasn't yet) and opens the widget once booted. No facade mounted (chat
// disabled / TPA / WDH) ⇒ no listener ⇒ no-op, same as the old
// `if (window.zE)` guard.
const handleChatTriggerClick = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(ZENDESK_OPEN_EVENT));
};

export const IconLinksGrid = ({
  items = [],
  includeLiveChatTrigger,
  liveChatTriggerConfig,
  liveChatTriggerItemNumber,
}: IconLinksGridProps) => {
  const getAllItems = (): IconLinkItem[] => {
    if (!includeLiveChatTrigger || !liveChatTriggerConfig) {
      return items;
    }
    const itemsWithTrigger = [...items];
    const index = (liveChatTriggerItemNumber ?? 0) - 1;

    if (index >= itemsWithTrigger.length || !liveChatTriggerItemNumber) {
      itemsWithTrigger.push({
        id: "chat-trigger",
        ...liveChatTriggerConfig,
      });
    } else {
      itemsWithTrigger.splice(index, 0, {
        id: "chat-trigger",
        ...liveChatTriggerConfig,
      });
    }
    return itemsWithTrigger;
  };

  const allItems = getAllItems();
  const totalRows = Math.ceil(allItems.length / 2);

  return (
    <div className="mb-5 px-2.5">
      <div className="grid grid-cols-2">
        {allItems.map((item, index) => {
          const { label, icon, isRevShare, id, ...buttonProps } = item;

          const content = (
            <div>
              <div className="mb-3">
                <img src={icon} alt={label} className="mx-auto h-10 w-10" />
              </div>
              <p className="text-lg font-medium">{label}</p>
            </div>
          );

          if (id === "chat-trigger") {
            return (
              <button
                type="button"
                onClick={handleChatTriggerClick}
                className={getBorderStyles(index, totalRows)}
                key={`icon-link-grid-${index}`}
                data-testid="iconLinkGrid-LinkWrapper"
              >
                {content}
              </button>
            );
          }

          return (
            <BuilderLinkWrapper
              {...(buttonProps as any)}
              isRevShare={isRevShare}
              attributes={{
                className: getBorderStyles(index, totalRows),
              }}
              key={`icon-link-grid-${index}`}
              data-testid="iconLinkGrid-LinkWrapper"
            >
              {content}
            </BuilderLinkWrapper>
          );
        })}
      </div>
    </div>
  );
};

export const IconLinksGridDefinition = {
  component: IconLinksGrid,
  name: "IconLinksGrid",
  image: "https://unpkg.com/@mdi/svg@7.4.47/svg/grid.svg",
  inputs: [
    {
      name: "items",
      type: "list",
      subFields: [
        {
          name: "icon",
          type: "file",
          allowedFileTypes: ["svg"],
          required: true,
          helperText: "Upload an SVG icon for the item.",
        },
        {
          name: "label",
          type: "string",
          required: true,
          helperText: "The text label for the item.",
        },
        ...coreButtonInputs,
      ],
      helperText:
        "The array of items with an uploaded SVG icon, label, and link.",
    },
    {
      name: "includeLiveChatTrigger",
      type: "boolean",
      defaultValue: true,
    },
    {
      name: "liveChatTriggerConfig",
      type: "object",
      subFields: [
        {
          name: "icon",
          type: "file",
          allowedFileTypes: ["svg"],
          required: true,
          helperText: "Upload an SVG icon for the item.",
        },
        {
          name: "label",
          type: "string",
          required: true,
          helperText: "The text label for the item.",
        },
      ],
      showIf: `options.get('includeLiveChatTrigger') === true`,
    },
    {
      name: "liveChatTriggerItemNumber",
      type: "number",
      showIf: `options.get('includeLiveChatTrigger') === true`,
      helperText:
        "Where in the list of icon links you want the chat trigger to appear. Will default to the last place in items list.",
    },
  ],
} satisfies RegisteredComponent;
